import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  agentLinkText,
  contactEventsQuery,
  contactQuery,
  contactSubscriptionsQuery,
  contactsShellQuery,
  contactsTableQuery,
  saveContact,
  type ContactForm,
  type ContactPanelTab,
  type ContactSubscription,
  type ContactsTab,
} from '../../api/settingsContacts'
import { dayMonthYear, stamp } from '../../inventoree/format'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Forbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings -> Contacts.
 *
 * Element cu element fata de `OwnersSupportModel` din
 * `public/js/settings/owners.js` (lista), fata de
 * `views/version-3/settings/contacts/modals/newContact.php` si
 * `forms/editContact.php` (formularul), fata de `tab_list.php` (cele doua file
 * ale randului desfasurat), si fata de `subscriptions.php` / `skus.php` /
 * `ContactEventsModel` (ce se vede in ele).
 *
 * CE E ALTFEL, SI DE CE
 * ---------------------
 *  - UN SINGUR FORMULAR. In portal erau doua machete cu aceleasi campuri:
 *    modala de creare si formularul de editare, adus prin
 *    `$modalBody.load('settings/contacts/editContact/<id>')`. Aceleasi campuri
 *    scrise de doua ori se despart in timp - si se despartisera deja: modala de
 *    creare avea bifa "Send agent download link by email", cea de editare nu.
 *  - COLOANA "ACTIONS" ARE NUMAI "EDIT". Butonul "Remove" de acolo chema
 *    `settings/companies/deleteOwner`, adica `Owners_model::softDelete($id)` -
 *    un `UPDATE ... WHERE ID = ?` fara firma, care sterge contactul oricarei
 *    firme. Nu se muta asa cum e, si nici nu se repara in treacat: are nevoie
 *    de dreptul lui si de o poarta pe firma, si aia e o hotarare. Un buton
 *    care nu face nimic e mai rau decat lipsa lui.
 *  - "Send agent download link by email": bifa din modala de creare a
 *    revenit (11 septembrie 2026), ca in `newContact.php` - scrisoarea pleaca
 *    prin `POST /settings/contacts` cu `send_agent_down_link=on`, dupa ce
 *    randul e scris. "Remarketing Message" nu s-a mutat - vezi capul lui
 *    `ContactsSettings` in API.
 */

const CONTACTS_TABLE = 'settings-contacts'

const EVENTS_TABLE = 'settings-contact-events'

export function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** `moment(...).format('D MMM YYYY, HH:mm')`, ca in `OwnersSupportModel`. */
export function whenText(value: unknown): string {
  const when = stamp(value)
  if (!when) return ''
  const hh = String(when.getHours()).padStart(2, '0')
  const mm = String(when.getMinutes()).padStart(2, '0')
  return `${dayMonthYear(when)}, ${hh}:${mm}`
}

/**
 * `OwnersSupportModel` din owners.js, coloana cu coloana, plus coloana 0 de
 * desfasurare - randul se deschide, ca `ExpandRow.openDetails` in portal.
 */
export function contactColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 30,
      className: 'center',
      html: (row) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(row.id)}" data-type="">` +
        '<i class="fas fa-plus-circle"></i></center>',
    },
    {
      index: 1,
      title: 'Firstname',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => `<strong>${esc(String(row.firstname ?? ''))}</strong>`,
      /* Steaua rosie a machetei de editare: un VIP se vede din lista. */
      rowClass: (row) => (Number(row.vip) === 1 ? 'vip' : undefined),
    },
    {
      index: 2,
      title: 'Lastname',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) =>
        `<strong>${esc(String(row.lastname ?? ''))}</strong>` +
        (Number(row.vip) === 1 ? ' <i class="fa fa-star" style="color:#FF0000" title="VIP"></i>' : ''),
    },
    { index: 3, title: 'Company', orderable: true, visible: true, className: 'a-left', html: (row) => esc(String(row.companyName ?? '')) },
    { index: 4, title: 'Email', orderable: true, visible: true, className: 'a-left', html: (row) => esc(String(row.email ?? '')) },
    { index: 5, title: 'Phone', orderable: true, visible: true, className: 'a-left', html: (row) => esc(String(row.phone ?? '')) },
    { index: 6, title: 'Mobile Phone', orderable: true, visible: true, className: 'a-left', html: (row) => esc(String(row.mobilePhone ?? '')) },
    {
      index: 7,
      title: 'Created',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) =>
        esc(whenText(row.createDate)) +
        (String(row.createdBy ?? '') !== ''
          ? `<p style="color:#52bbb6">by ${esc(String(row.createdBy))}</p>`
          : ''),
    },
    { index: 8, title: 'Last Edit', orderable: true, visible: true, className: 'a-left', html: (row) => esc(whenText(row.lastEdit)) },
    {
      index: 9,
      title: 'Actions',
      orderable: false,
      visible: true,
      className: 'a-left',
      html: (row) =>
        `<button type="button" class="btn btn-xs btn-default od-edit-contact" data-owner="${Number(row.id)}">` +
        '<i class="fa fa-user" aria-hidden="true"></i> Edit</button>',
    },
  ]
}

/**
 * Coloana SQL a fiecarui indice, ALINIATA la ce vede omul.
 *
 * Harta veche (`Owners_model::buildTableColumns`) incepea cu `ID`, iar prima
 * coloana desenata e "Firstname" - deci un clic pe "Lastname" ordona dupa
 * prenume, si asa mai departe, cu un pas. Aici indicele 0 e coloana de
 * desfasurare (nu se ordoneaza dupa ea), si de la 1 incolo numele urmeaza
 * coloana.
 */
export const CONTACT_ORDER_NAMES = [
  '',
  'firstname',
  'lastname',
  'company_name',
  'email',
  'phone',
  'mobile_phone',
  'create_date',
  'last_edit',
  '',
]

/** `ContactEventsModel` din app.js: Date, Event, User. */
export function eventColumns(): TableColumn[] {
  return [
    { index: 0, title: '<i class="far fa-calendar"></i> Date', titleHtml: '<i class="far fa-calendar"></i> Date', orderable: true, visible: true, width: 150, className: 'td-small a-left', html: (row) => esc(whenText(row.date)) },
    { index: 1, title: 'Event', orderable: true, visible: true, width: 150, className: 'td-small a-left', html: (row) => esc(String(row.message ?? '')) },
    {
      index: 2,
      title: 'User',
      orderable: true,
      visible: true,
      width: 150,
      className: 'td-small a-left',
      /* Fara om in spate: "System", ca in modelul vechi. */
      html: (row) => (String(row.user ?? '') !== '' ? esc(String(row.user)) : '<strong>System</strong>'),
    },
  ]
}

export const EVENT_ORDER_NAMES = ['event_date', 'event_log', 'event_creator']

/** Ce spune serverul cand refuza - "Email is already used.", nu "Something went wrong.". */
function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

const EMPTY_FORM: ContactForm = {
  id: 0,
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  personal_phone: '',
  position: '',
  credit_card_authorization_number: '',
  city: '',
  state: '',
  zip_code: '',
  address: '',
  vip: 0,
}

/* -------------------------------------------------------------------------
 * Formularul (newContact.php + forms/editContact.php, scrise o singura data)
 * ---------------------------------------------------------------------- */

function ContactFields({ form, onChange, sendLink, onSendLink }: {
  form: ContactForm
  onChange(next: ContactForm): void
  /** `undefined` = formularul de editare, care n-avea bifa (`editContact.php`). */
  sendLink?: boolean
  onSendLink?(on: boolean): void
}) {
  const text = (name: keyof ContactForm, label: string, required = false, type = 'text') => (
    <div className="form-group">
      <label className="col-sm-3 control-label" htmlFor={`contact-${name}`}>
        {label} {required ? <span className="required">*</span> : null}
      </label>
      <div className="col-sm-9">
        <input
          id={`contact-${name}`}
          type={type}
          className="form-control input-sm"
          name={String(name)}
          required={required}
          value={String(form[name] ?? '')}
          onChange={(e) => onChange({ ...form, [name]: e.target.value })}
        />
      </div>
    </div>
  )

  return (
    <>
      {text('firstname', 'Firstname', true)}
      {text('lastname', 'Lastname', true)}
      {text('email', 'Email', true, 'email')}
      {text('phone', 'Phone')}
      {text('personal_phone', 'Mobile Phone')}
      {text('position', 'Position')}
      {text('credit_card_authorization_number', 'Credit Card Number')}
      {text('city', 'City')}
      {text('state', 'State')}
      {text('zip_code', 'ZIP')}
      <div className="form-group">
        <label className="col-sm-3 control-label" htmlFor="contact-address">
          Address
        </label>
        <div className="col-sm-9">
          <textarea
            id="contact-address"
            className="form-control"
            style={{ maxWidth: '100%' }}
            name="address"
            value={form.address}
            onChange={(e) => onChange({ ...form, address: e.target.value })}
          />
        </div>
      </div>
      {sendLink !== undefined ? (
        <div className="form-group">
          <label className="col-sm-3 control-label"></label>
          <div className="col-sm-9">
            <label style={{ fontWeight: 400 }}>
              <input
                type="checkbox"
                className="icheck"
                name="send_agent_down_link"
                checked={sendLink}
                onChange={(e) => onSendLink?.(e.target.checked)}
              />{' '}
              <span style={{ fontSize: '1.2rem' }}>Send agent download link by email</span>
            </label>
          </div>
        </div>
      ) : null}
      <div className="form-group">
        <label className="col-sm-3 control-label"></label>
        <div className="col-sm-9">
          <label style={{ fontWeight: 400 }}>
            <input
              type="checkbox"
              className="icheck-vip"
              name="vip"
              value="1"
              checked={form.vip === 1}
              onChange={(e) => onChange({ ...form, vip: e.target.checked ? 1 : 0 })}
            />{' '}
            <span style={{ fontSize: '1.2rem', color: '#FF0000', fontWeight: 700 }}>
              VIP <i className="fa fa-star"></i>
            </span>
          </label>
        </div>
      </div>
    </>
  )
}

function ContactModal({ id, open, onClose, onSaved }: {
  id: number
  open: boolean
  onClose(): void
  onSaved(): void
}) {
  const existing = useQuery(contactQuery(open ? id : 0))
  const [form, setForm] = useState<ContactForm>(EMPTY_FORM)
  const [sendLink, setSendLink] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setError('')
    setSendLink(false)
    setForm(id > 0 ? (existing.data?.contact ?? EMPTY_FORM) : EMPTY_FORM)
  }, [open, id, existing.data])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError('')
    const fields: Record<string, string> = {
      firstname: form.firstname,
      lastname: form.lastname,
      email: form.email,
      phone: form.phone,
      personal_phone: form.personal_phone,
      position: form.position,
      credit_card_authorization_number: form.credit_card_authorization_number,
      city: form.city,
      state: form.state,
      zip_code: form.zip_code,
      address: form.address,
    }
    /* Bifa nebifata NU se trimite, ca in formularul vechi: serverul citeste
       lipsa ei ca "am scos-o". */
    if (form.vip === 1) fields.vip = '1'
    if (id > 0) fields.id = String(id)
    else if (sendLink) fields.send_agent_down_link = 'on'

    void saveContact(fields)
      .then((saved) => {
        setBusy(false)
        toast(id > 0 ? 'Contact updated.' : 'Contact created.', 'Success')
        if (saved.agentLink) {
          const said = agentLinkText(saved.agentLink)
          toast(said.text, 'Download agent link', said.ok ? 'success' : 'error')
        }
        onSaved()
        onClose()
      })
      .catch((err: unknown) => {
        setBusy(false)
        setError(failed(err))
      })
  }

  return (
    <Modal id="newContactModal" open={open} onClose={onClose} title={id > 0 ? 'Edit contact' : 'New contact'}>
      <form className="form-horizontal" id="newContactForm" onSubmit={submit}>
        <div className="form-errors">
          {error !== '' ? (
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {error}
            </div>
          ) : null}
        </div>
        {id > 0 && !existing.data ? (
          <Spinner />
        ) : (
          <ContactFields
            form={form}
            onChange={setForm}
            sendLink={id > 0 ? undefined : sendLink}
            onSendLink={setSendLink}
          />
        )}
        <div className="form-group">
          <div className="col-sm-offset-3 col-sm-9 aright">
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>{' '}
            <button type="submit" className="btn btn-primary" disabled={busy}>
              Save
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

/* -------------------------------------------------------------------------
 * Panoul unui contact (tab_list.php): Subscriptions si Events
 * ---------------------------------------------------------------------- */

/** Cartonasul unui abonament, ca in `subscriptions.php` / `skus.php`. */
function PlanCard({ plan }: { plan: ContactSubscription }) {
  const kind = plan.status === 'active' ? 'success' : plan.status === 'cancelled' ? 'default' : 'danger'
  const label = plan.status === 'active' ? 'Active' : plan.status === 'cancelled' ? 'Cancelled' : 'Expired'

  return (
    <div className="col-lg-3">
      <ul className={`plan corner-flip flip-gray ${kind}`}>
        <li className="plan-name">{plan.name}</li>
        <li>
          Status <label className={`label label-${kind}`}>{label}</label>
        </li>
        <li>
          Type{' '}
          <label className="label label-default">
            {plan.supportLicense === -1 ? 'Unlimited' : `${plan.supportLicense} days`}
          </label>
        </li>
        <li>
          Date of purchase <label className="label label-default">{plan.purchaseDate || 'N/A'}</label>
        </li>
        <li>
          {plan.endDate !== '' ? (
            <>
              End of subscription <label className="label label-default">{plan.endDate}</label>
            </>
          ) : null}
        </li>
      </ul>
    </div>
  )
}

function SubscriptionsPanel({ contactId }: { contactId: number }) {
  const page = useQuery(contactSubscriptionsQuery(contactId))

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const both = [
    { rows: page.data.subscriptions, key: 'subscriptions' },
    { rows: page.data.skus, key: 'skus' },
  ]

  return (
    <>
      {both.map(({ rows, key }) =>
        rows.length > 0 ? (
          <div className="row pricing" key={key}>
            {rows.map((plan, index) => (
              <PlanCard key={`${key}-${plan.skuId}-${index}`} plan={plan} />
            ))}
          </div>
        ) : (
          <div className="alert alert-info" key={key}>
            <i className="fas fa-info-circle" aria-hidden="true"></i> No subscriptions
          </div>
        ),
      )}
    </>
  )
}

function EventsPanel({ contactId }: { contactId: number }) {
  const [state, setState] = useTableState(EVENTS_TABLE, {
    start: 0,
    length: 25,
    /* `order: [[0, 'desc']]` - implicitul lui `initDetailsTable`. */
    order: { column: 0, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => eventColumns(), [])
  const table = useQuery({
    ...contactEventsQuery(contactId, {
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: EVENT_ORDER_NAMES[state.order.column] ?? 'event_date',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  return (
    <DataTable
      id={`${EVENTS_TABLE}-${contactId}`}
      columns={columns}
      rows={rows}
      total={table.data?.recordsFiltered ?? 0}
      fetching={table.isFetching}
      error={table.error ? table.error.message : null}
      onRetry={() => void table.refetch()}
      state={state}
      onState={setState}
      rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.id)}`)}
      emptyHint="Nothing has happened to this contact yet."
    />
  )
}

function ContactPanel({ contactId, tabs }: { contactId: number; tabs: ContactPanelTab[] }) {
  const [tab, setTab] = useState<ContactPanelTab['id'] | null>(null)
  const active = tab ?? tabs[0]?.id ?? null

  if (active === null) return <Forbidden />

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {tabs.map((item) => (
            <li key={item.id}>
              <a
                href={`/app/settings/contacts`}
                className={item.id === active ? 'action_button active' : 'action_button'}
                onClick={(e) => {
                  e.preventDefault()
                  setTab(item.id)
                }}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {active === 'subscriptions' ? <SubscriptionsPanel contactId={contactId} /> : null}
          {active === 'events' ? <EventsPanel contactId={contactId} /> : null}
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------
 * Ecranul
 * ---------------------------------------------------------------------- */

function SidebarTab({ tab }: { tab: ContactsTab }) {
  return (
    <li className="menu-tab active">
      <a id={tab.id} href={`/app/${tab.href}`} onClick={(e) => e.preventDefault()}>
        <i className={tab.icon} aria-hidden="true"></i> {tab.label}
      </a>
    </li>
  )
}

export function ContactsSettingsPage() {
  const query = useQueryClient()
  const shell = useQuery(contactsShellQuery)
  const [editing, setEditing] = useState<number | null>(null)
  const [state, setState] = useTableState(CONTACTS_TABLE, {
    start: 0,
    length: 25,
    /* `order: [[1, 'desc']]` din owners.js - a doua coloana, adica Lastname
       odata ce indicii sunt aliniati la ce vede omul. */
    order: { column: 1, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => contactColumns(), [])
  const table = useQuery({
    ...contactsTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: CONTACT_ORDER_NAMES[state.order.column] ?? 'firstname',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]
  const refresh = () => {
    void query.invalidateQueries({ queryKey: ['settings', 'contacts'] })
  }

  return (
    <>
      <nav className="od-tabbar">
        <ul className="od-tabbar-nav">
          {shell.data.tabs.length > 0 ? <li className="menu-header">{shell.data.tabs[0].group}</li> : null}
          {shell.data.tabs.map((tab) => (
            <SidebarTab key={tab.id} tab={tab} />
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </nav>
      {/*
        Un singur ascultator pentru butoanele "Edit" din coloana Actions: ele
        sunt HTML scris de coloana, ca in DataTables, deci nu au React pe ele -
        exact cum in portal butonul era desenat de `initOwnerActions()` si prins
        cu un `on('click', ...)` delegat.
      */}
      <div
        className="page-content-wrapper"
        onClick={(event) => {
          const button = (event.target as HTMLElement).closest('.od-edit-contact')
          if (!button) return
          const owner = Number(button.getAttribute('data-owner') ?? 0)
          if (owner > 0) setEditing(owner)
        }}
      >
        <button type="button" id="newContact" className="pull-right btn btn-success btn-sm" title="Create new contact" onClick={() => setEditing(0)}>
          <i className="fa fa-user-plus" aria-hidden="true"></i> Create
        </button>
        <div className="clearfix"></div>
        <DataTable
          id={CONTACTS_TABLE}
          columns={columns}
          rows={rows}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          error={table.error ? table.error.message : null}
          onRetry={() => void table.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.id)}`)}
          emptyHint="A contact is the person a device or a ticket belongs to."
          details={(row) => <ContactPanel contactId={Number(row.id)} tabs={shell.data.panelTabs} />}
        />
      </div>
      <ContactModal
        id={editing ?? 0}
        open={editing !== null}
        onClose={() => setEditing(null)}
        onSaved={refresh}
      />
    </>
  )
}
